<template>
  <div class="historico-licenca">
    <div class="p-formgrid p-grid p-ai-end">
      <div class="p-field p-fluid p-col-12 p-md-6">
        <input-with-label label="Mostrar" v-slot="l">
          <MultiSelect
            v-model="categorias"
            :options="categoriasDisponiveis"
            optionLabel="descricao"
            optionValue="categoria"
            placeholder="Todas"
            display="chip"
            :id="l.id"
          />
        </input-with-label>
      </div>
      <div class="p-field p-col-12 p-md-6 p-text-right">
        <Button
          icon="pi pi-refresh"
          label="Atualizar"
          class="p-button-rounded p-button-text"
          :disabled="stateLoading"
          @click="carregarHistorico()"
        />
      </div>
    </div>

    <DataTable
      class="tabela-historico"
      :value="historico"
      dataKey="idHistoricoLicenca"
      responsiveLayout="stack"
      :showGridlines="true"
      :loading="stateLoading"
      :paginator="historico.length > linhasPorPagina"
      :rows="linhasPorPagina"
      :rowClass="getClasseLinha"
      v-model:first="primeiraLinha"
    >
      <template #empty>
        <span>Nenhum registro para os filtros selecionados.</span>
      </template>
      <Column header="Data" style="width: 11rem">
        <template #body="slotProps">
          <span v-text="formatarData(slotProps.data.timestampOperacao)" />
        </template>
      </Column>
      <Column header="Categoria" style="width: 9rem">
        <template #body="slotProps">
          <span
            :class="
              'historico-categoria historico-categoria-' +
                slotProps.data.categoria.toLowerCase()
            "
            v-text="getDescricaoCategoria(slotProps.data.categoria)"
          />
        </template>
      </Column>
      <Column header="Quem" style="width: 13rem">
        <template #body="slotProps">
          <div v-text="slotProps.data.nomeUsuario || '—'" />
          <small
            class="historico-origem-operacao"
            v-if="slotProps.data.origem"
            v-text="getDescricaoOrigem(slotProps.data.origem)"
          />
          <small
            class="historico-origem-operacao"
            v-else-if="slotProps.data.nomeUsuario"
            v-title="
              'Registro anterior à rastreabilidade: autor atribuído pela regra de carga (robô ou administrador do cliente)'
            "
            v-text="'Autoria presumida'"
          />
        </template>
      </Column>
      <Column header="Órgão" style="width: 7rem">
        <template #body="slotProps">
          <span v-text="slotProps.data.nomeOrgao || ''" />
        </template>
      </Column>
      <Column header="O que aconteceu">
        <template #body="slotProps">
          <div>
            <i
              v-if="slotProps.data.flPosProtocolo"
              class="pi pi-exclamation-triangle historico-alerta"
              v-title="'Alteração depois do protocolo no órgão'"
            ></i>
            <span v-text="slotProps.data.descricao" />
            <a
              v-if="slotProps.data.idLicencaOrigem"
              class="p-ml-1"
              :href="getUrlLicenca(slotProps.data.idLicencaOrigem)"
              target="_blank"
              v-text="'Abrir a licença ' + slotProps.data.idLicencaOrigem"
            />
          </div>
          <div class="historico-antes-depois" v-if="slotProps.data.campo">
            <span
              class="historico-valor-anterior"
              v-text="slotProps.data.valorAnterior || '—'"
            />
            <i class="pi pi-arrow-right" aria-hidden="true"></i>
            <strong v-text="slotProps.data.valorNovo || '—'" />
          </div>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  reactive,
  toRefs,
  onMounted,
  computed,
  watch,
  Ref
} from "vue";
import { useRouter } from "vue-router";
import { formatDateTime } from "../../../modules/utils";
import {
  CATEGORIAS_HISTORICO,
  ORIGENS_OPERACAO,
  CategoriaHistorico,
  HistoricoLicencaPayload,
  OrigemOperacao,
  useLicencaService
} from "../../../modules/business/licenca/licencaBusiness";

interface HistoricoLicencaState {
  historico: Array<HistoricoLicencaPayload>;
  categorias: Array<CategoriaHistorico>;
  primeiraLinha: number;
  loadings: Array<Ref<boolean>>;
}

/**
 * Aba "Histórico" do cadastro da licença: quem fez o quê (cliente, robô,
 * monitoramento do órgão, rotinas), com antes e depois das trocas de veículo,
 * peso e dimensões. O monitoramento (consultas de situação) é a maior parte
 * das linhas; todas as categorias começam marcadas.
 */
export default defineComponent({
  props: {
    idLicenca: {
      type: Number,
      required: true
    },
    parentLoading: Boolean
  },
  setup(props) {
    const state = reactive<HistoricoLicencaState>({
      historico: [],
      categorias: ["CLIENTE", "ROBO", "MONITORAMENTO", "SISTEMA"],
      primeiraLinha: 0,
      loadings: []
    });

    const router = useRouter();

    const linhasPorPagina = 50;

    const categoriasDisponiveis = CATEGORIAS_HISTORICO;

    const stateLoading = computed(() => {
      return state.loadings.length !== 0 || !!props.parentLoading;
    });

    const getTempo = (linha: HistoricoLicencaPayload) =>
      linha.timestampOperacao ? new Date(linha.timestampOperacao).getTime() : 0;

    const carregarHistorico = () => {
      const { data, loading, getHistoricoLicenca } = useLicencaService();
      state.loadings.push(loading);
      // sem categoria marcada = todas
      getHistoricoLicenca(props.idLicenca, state.categorias)
        .then(() => {
          // mais recente primeiro; sort estavel: linhas da mesma alteracao
          // (mesmo horario) ficam na ordem gravada, com o alerta antes
          state.historico = [
            ...((data.value as Array<HistoricoLicencaPayload>) ?? [])
          ].sort((a, b) => getTempo(b) - getTempo(a));
          state.primeiraLinha = 0;
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        })
        .catch(() => {
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        });
    };

    watch(
      () => [...state.categorias],
      () => carregarHistorico()
    );

    const formatarData = (valor?: Date | string | null) => {
      if (!valor) {
        return "";
      }
      return formatDateTime(valor instanceof Date ? valor : new Date(valor));
    };

    const getDescricaoCategoria = (categoria: CategoriaHistorico) =>
      CATEGORIAS_HISTORICO.find((c) => c.categoria === categoria)?.descricao ??
      categoria;

    const getDescricaoOrigem = (origem: OrigemOperacao) =>
      ORIGENS_OPERACAO[origem] ?? origem;

    const getClasseLinha = (linha: HistoricoLicencaPayload) =>
      linha.flPosProtocolo ? "historico-pos-protocolo" : "";

    const getUrlLicenca = (idLicenca: number) =>
      router.resolve({
        name: "licenca-edit",
        params: { idLicenca: idLicenca + "" }
      }).href;

    onMounted(() => carregarHistorico());

    return {
      stateLoading,
      linhasPorPagina,
      categoriasDisponiveis,
      carregarHistorico,
      formatarData,
      getDescricaoCategoria,
      getDescricaoOrigem,
      getClasseLinha,
      getUrlLicenca,
      ...toRefs(state)
    };
  }
});
</script>

<style scoped lang="scss">
.historico-categoria {
  display: inline-block;
  padding: 0.125rem 0.5rem;
  border-radius: $radius-full;
  font-size: $font-size-xs;
  font-weight: $font-weight-semibold;
  white-space: nowrap;
}

.historico-categoria-cliente {
  background: $primary-light;
  color: $primary;
}

.historico-categoria-robo {
  background: $success-light;
  color: $success;
}

.historico-categoria-monitoramento {
  background: $info-light;
  color: darken($info, 20%);
}

.historico-categoria-sistema {
  background: $border-color-light;
  color: $text-secondary;
}

.historico-origem-operacao {
  color: $text-secondary;
}

.historico-antes-depois {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.25rem;

  .pi {
    font-size: $font-size-xs;
    color: $text-muted;
  }
}

.historico-valor-anterior {
  color: $text-secondary;
  text-decoration: line-through;
}

.historico-alerta {
  color: $warning;
  margin-right: 0.375rem;
}

// bordas em todas as celulas e linhas alternadas (branca e cinza) para facilitar a leitura
.tabela-historico :deep(.p-datatable-table) {
  border: 1px solid darken($border-color, 8%) !important;
  border-collapse: collapse;
}

.tabela-historico :deep(.p-datatable-thead > tr > th),
.tabela-historico :deep(.p-datatable-tbody > tr > td) {
  border: 1px solid darken($border-color, 8%) !important;
  vertical-align: top;
}

.tabela-historico :deep(.p-datatable-tbody > tr:nth-child(odd)) {
  background: $bg-surface !important;
}

.tabela-historico :deep(.p-datatable-tbody > tr:nth-child(even)) {
  background: $bg-color !important;
}

// alteracao feita depois do protocolo continua em amarelo, por cima do zebrado
.tabela-historico
  :deep(.p-datatable-tbody > tr.historico-pos-protocolo:nth-child(n)) {
  background: $warning-bg !important;
}
</style>
